import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, FileText, AlertCircle, CheckCircle2, Loader2, Eye, Clock, RotateCcw, Download } from "lucide-react";
import SwissBadge from "@/components/SwissBadge";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Lightweight unauthenticated axios — never sends credentials/cookies.
const pub = axios.create({ baseURL: API, withCredentials: false });

export default function PublicCaseView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState("");
  const [signed, setSigned] = useState(null);

  // Fetch case data
  useEffect(() => {
    if (!token) return;
    pub
      .get(`/public/cases/${token}`)
      .then(({ data }) => {
        setData(data);
        if (data?.magic_link?.receipt_signed_at) {
          setSigned({
            at: data.magic_link.receipt_signed_at,
            name: data.magic_link.receipt_signer_name,
          });
        }
        setSignerName((data?.customer_name || "").trim());
      })
      .catch((e) => {
        const code = e.response?.status;
        if (code === 410) setErr("Dieser Link ist leider abgelaufen.");
        else if (code === 404) setErr("Link nicht gefunden — bitte beim Sachverständigen einen neuen anfordern.");
        else setErr("Der Link konnte nicht geladen werden. Versuchen Sie es bitte später erneut.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  // Signature pad
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const dirty = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = "#0b1320";
    ctx.lineWidth = 2.4;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
  }, [signed, data]);

  const onPointer = (e, type) => {
    e.preventDefault();
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    const ctx = c.getContext("2d");
    if (type === "start") {
      drawing.current = true;
      last.current = { x, y };
    } else if (type === "move" && drawing.current) {
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(x, y);
      ctx.stroke();
      last.current = { x, y };
      dirty.current = true;
    } else if (type === "end") {
      drawing.current = false;
      if (dirty.current) setSignatureUrl(c.toDataURL("image/png"));
    }
  };

  const clearSig = () => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setSignatureUrl("");
    dirty.current = false;
  };

  const ready = useMemo(
    () => Boolean(signerName.trim().length >= 2 && signatureUrl && confirmed),
    [signerName, signatureUrl, confirmed]
  );

  const submit = async () => {
    if (!ready) return;
    setSigning(true);
    try {
      const { data } = await pub.post(`/public/cases/${token}/receipt`, {
        signer_name: signerName.trim(),
        signature_data_url: signatureUrl,
      });
      setSigned({ at: data.receipt_signed_at, name: data.receipt_signer_name });
    } catch (e) {
      const code = e.response?.status;
      if (code === 409) setSigned({ at: null, name: signerName.trim() });
      else alert("Empfangsbestätigung konnte nicht gesendet werden. Bitte erneut versuchen.");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-24 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade Gutachten…
        </div>
      </Shell>
    );
  }

  if (err) {
    return (
      <Shell>
        <div className="rounded-3xl border border-rose-200 bg-rose-50/60 p-8 text-center" data-testid="public-case-error">
          <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-rose-600 text-white flex items-center justify-center">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">Link nicht verfügbar</h1>
          <p className="text-sm text-zinc-700 mt-2">{err}</p>
        </div>
      </Shell>
    );
  }

  const pdfUrl = `${API}/public/cases/${token}/pdf`;

  return (
    <Shell>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className="h-12 w-12 rounded-2xl brand-gradient-bg flex items-center justify-center shrink-0">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">
              Ihr Gutachten · {data?.case_reference || data?.case_number}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 text-zinc-900">
              {data?.customer_name ? `Hallo ${data.customer_name},` : "Ihr Gutachten ist bereit"}
            </h1>
            <p className="text-sm text-zinc-600 mt-1.5 max-w-2xl">
              das Kfz-Gutachten zu Ihrem Fahrzeug ist fertig. Bitte prüfen Sie den Inhalt unten und bestätigen
              Sie unten den Empfang. Bei Fragen wenden Sie sich bitte an Ihren Sachverständigen.
            </p>
          </div>
        </div>

        {/* Vehicle + insurer card */}
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <Card label="Fahrzeug">
            <div className="text-sm font-semibold text-zinc-900">
              {(data?.vehicle?.make || "") + " " + (data?.vehicle?.model || "")}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5 font-mono uppercase">
              {data?.vehicle?.license_plate || "—"}
            </div>
          </Card>
          <Card label="Versicherung">
            <div className="text-sm font-semibold text-zinc-900">
              {data?.insurance?.insurer_name || "—"}
            </div>
            {data?.insurance?.claim_number ? (
              <div className="text-xs text-zinc-500 mt-0.5">Schadennr. {data.insurance.claim_number}</div>
            ) : null}
          </Card>
        </div>

        {/* PDF viewer */}
        <div className="rounded-3xl border border-zinc-200 bg-white overflow-hidden shadow-sm mb-6" data-testid="public-pdf-viewer">
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-sm font-medium text-zinc-800">
              <FileText className="h-4 w-4 text-cyan-700" />
              {data?.final_report?.filename || "Gutachten.pdf"}
              {data?.final_report?.version ? (
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 ml-1">
                  · v{data.final_report.version}
                </span>
              ) : null}
            </div>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-800"
              data-testid="public-pdf-download"
            >
              <Download className="h-3.5 w-3.5" /> Herunterladen
            </a>
          </div>
          <iframe
            src={pdfUrl}
            title="Gutachten-PDF"
            className="w-full h-[68vh] sm:h-[74vh] bg-zinc-50"
            data-testid="public-pdf-iframe"
          />
        </div>

        {/* Receipt */}
        {signed?.at ? (
          <SignedConfirmation signed={signed} />
        ) : (
          <div className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 via-white to-cyan-50/30 p-6 sm:p-8" data-testid="public-receipt-form">
            <div className="flex items-start gap-3 mb-5">
              <div className="h-10 w-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700">
                  Empfangsbestätigung
                </div>
                <h2 className="font-display text-xl font-bold tracking-tight text-zinc-900 mt-1">
                  Ich habe das Gutachten erhalten
                </h2>
                <p className="text-sm text-zinc-600 mt-1.5 max-w-xl">
                  Mit Ihrer Unterschrift bestätigen Sie, dass Sie das Gutachten zum oben genannten Vorgang
                  erhalten haben. Es entstehen Ihnen dadurch keine zusätzlichen Kosten.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-zinc-200 p-5">
              <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">
                Name *
              </label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Ihr vollständiger Name"
                className="mt-1.5 w-full h-11 px-3 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-500"
                data-testid="public-signer-name"
              />

              <div className="mt-5 flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">
                  Unterschrift *
                </div>
                <button
                  type="button"
                  onClick={clearSig}
                  className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
                  data-testid="public-signature-clear"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Löschen
                </button>
              </div>
              <canvas
                ref={canvasRef}
                className="w-full h-44 sm:h-48 mt-1.5 rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50 touch-none"
                style={{ touchAction: "none" }}
                onMouseDown={(e) => onPointer(e, "start")}
                onMouseMove={(e) => onPointer(e, "move")}
                onMouseUp={(e) => onPointer(e, "end")}
                onMouseLeave={(e) => onPointer(e, "end")}
                onTouchStart={(e) => onPointer(e, "start")}
                onTouchMove={(e) => onPointer(e, "move")}
                onTouchEnd={(e) => onPointer(e, "end")}
                data-testid="public-signature-canvas"
              />

              <label className="mt-5 flex items-start gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-emerald-600"
                  data-testid="public-confirm-checkbox"
                />
                <span className="text-zinc-800">
                  Ich bestätige, das Gutachten erhalten und durchgesehen zu haben.
                </span>
              </label>

              <button
                type="button"
                onClick={submit}
                disabled={!ready || signing}
                className="mt-4 w-full h-12 rounded-md text-base font-semibold brand-gradient-bg text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                data-testid="public-receipt-submit"
              >
                {signing ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                Empfang verbindlich bestätigen
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 mb-12">
          <p className="text-[11px] text-zinc-400">
            Dieser Link ist persönlich und vertraulich. Bitte nicht weitergeben.
          </p>
          {data?.magic_link?.expires_at ? (
            <p className="text-[11px] text-zinc-400 mt-1 inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> Gültig bis {new Date(data.magic_link.expires_at).toLocaleDateString("de-DE")}
            </p>
          ) : null}
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50/30 via-white to-emerald-50/30">
      <header className="border-b border-zinc-100 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg brand-gradient-bg flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-white" strokeWidth={2.4} />
            </div>
            <span className="font-display font-bold tracking-tight text-zinc-900">Easy Gutachter</span>
          </div>
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-zinc-400">Endkunden-Portal</span>
        </div>
      </header>
      <main className="px-5 sm:px-8 py-8">{children}</main>
      <footer className="flex justify-center pb-8">
        <SwissBadge />
      </footer>
    </div>
  );
}

function Card({ label, children }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-3" data-testid={`public-card-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SignedConfirmation({ signed }) {
  return (
    <div className="rounded-3xl border border-emerald-300 bg-emerald-50/60 p-6 sm:p-8 text-center" data-testid="public-receipt-confirmed">
      <div className="mx-auto h-12 w-12 rounded-2xl bg-emerald-600 text-white flex items-center justify-center mb-3">
        <CheckCircle2 className="h-6 w-6" />
      </div>
      <h2 className="font-display text-xl font-bold tracking-tight text-zinc-900">Vielen Dank — Empfang bestätigt</h2>
      <p className="text-sm text-zinc-700 mt-2">
        {signed?.name ? `${signed.name}, ` : ""}wir haben Ihre Empfangsbestätigung notiert.
      </p>
      {signed?.at ? (
        <p className="text-[11px] text-zinc-500 mt-2 inline-flex items-center gap-1">
          <Eye className="h-3 w-3" /> Bestätigt am {new Date(signed.at).toLocaleString("de-DE")}
        </p>
      ) : null}
    </div>
  );
}
